import * as React from 'react';
const CHUNK_RELOAD_KEY = 'chunkLoadErrorReloadAt';
// webpack rejects a chunk that failed to download or to parse with a 'ChunkLoadError'
const isChunkLoadError = (error) => !!error && error.name === 'ChunkLoadError';
// After a deploy, an already-open tab (or HTML cached by Cloudflare) still references chunk
// hashes that no longer exist — one fresh page load fixes that. At most one automatic reload
// per tab per minute, so a chunk that can never load (e.g. a browser too old to parse it)
// falls through to the error boundary instead of reloading forever.
const reloadOnceForChunkError = () => {
    try {
        const last = Number(window.sessionStorage.getItem(CHUNK_RELOAD_KEY)) || 0;
        if (Date.now() - last < 60 * 1000)
            return false;
        window.sessionStorage.setItem(CHUNK_RELOAD_KEY, String(Date.now()));
    }
    catch (e) {
        return false; // no storage → no loop guard → don't auto-reload
    }
    window.location.reload();
    return true;
};
/**
 * Minimal replacement for react-loadable (not React 18 compatible).
 * Loads the chunk on mount and renders the Loading component with
 * { isLoading, error } in the meantime. A load failure is rethrown to the
 * nearest error boundary (after one automatic reload for a ChunkLoadError).
 */
const asyncComponent = (loader, Loading) => class AsyncComponent extends React.Component {
    constructor() {
        super(...arguments);
        Object.defineProperty(this, "mounted", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: false
        });
        Object.defineProperty(this, "state", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: { Component: null, error: null }
        });
    }
    componentDidMount() {
        this.mounted = true;
        loader().then((mod) => {
            const Component = mod && mod.default ? mod.default : mod;
            if (this.mounted)
                this.setState({ Component });
        }, (error) => {
            if (isChunkLoadError(error) && reloadOnceForChunkError())
                return;
            if (this.mounted)
                this.setState({ error });
        });
    }
    componentWillUnmount() {
        this.mounted = false;
    }
    render() {
        const { Component, error } = this.state;
        if (Component)
            return React.createElement(Component, { ...this.props });
        // ADV-286: rethrow to the nearest error boundary, as React.lazy does — it shows a fallback
        // with a retry and reports to Sentry. Before, LoadingPage rendered an empty box (its error
        // handling only runs on mount, before the error exists) and nothing was reported.
        if (error)
            throw error;
        if (!Loading)
            return null;
        return React.createElement(Loading, { isLoading: !error, error: error });
    }
};
export default asyncComponent;
